import { MINIAPP_PACKAGE_ID } from '@/consts/configs';
import { ErrorMessages } from '@/consts/errors';
import { UPLOAD_URL } from '@/consts/urls';
import { getContext } from '@/context';
import { getAppStore, setAppStore } from '@/store';
import { ToastType } from '@/store/toast/types';
import sendUploadFailureAnalytics from '@/utils/analytics/sendUploadFailureAnalytics';

const CDN_UPLOAD_URL = `${UPLOAD_URL}/files`;
const CDN_BUCKET = 'cloud-storage';
const MAX_UPLOAD_ATTEMPTS = 3;
const UNAUTHORIZED_STATUS = 401;

const buildUploadHeaders = (): Record<string, string> => ({
  ...getContext().utils.api.getHeaders(),
  'x-touchpoint': MINIAPP_PACKAGE_ID,
});

const buildUploadBody = (file: File): FormData => {
  const body = new FormData();
  body.append('type', CDN_BUCKET);
  body.append('file', file);

  return body;
};

// A present-but-expired token uploads fine locally but the server answers 401.
// Refresh the token (surfacing the wait as `isGenerating`) so the retry carries
// a fresh one.
const refreshAccessToken = async () => {
  try {
    setAppStore({ isGenerating: true });
    await getContext().handlers.refreshToken();
  } finally {
    setAppStore({ isGenerating: false });
  }
};

// Thrown when the CDN still rejects the upload after a token refresh — the token
// isn't the problem, so there's nothing to retry.
class UnauthorizedUploadError extends Error {
  constructor() {
    super('CDN upload unauthorized after token refresh');
    this.name = 'UnauthorizedUploadError';
  }
}

const postUpload = (file: File): Promise<Response> =>
  fetch(CDN_UPLOAD_URL, {
    method: 'POST',
    headers: buildUploadHeaders(),
    body: buildUploadBody(file),
  });

const parseUploadUrl = async (response: Response): Promise<string> => {
  const { result } = await response.json();
  const url = result?.download_url;
  if (!url) {
    throw new Error('CDN upload response is missing a download URL');
  }

  return url;
};

const requestUpload = async (file: File): Promise<string> => {
  let response = await postUpload(file);

  // The raw Response exposes the real HTTP status, so a stale token can be
  // refreshed and the upload retried once. If it's still 401 afterwards it isn't
  // a token problem — fail fast instead of hammering the endpoint.
  if (response.status === UNAUTHORIZED_STATUS) {
    await refreshAccessToken();
    response = await postUpload(file);

    if (response.status === UNAUTHORIZED_STATUS) {
      throw new UnauthorizedUploadError();
    }
  }

  if (!response.ok) {
    throw new Error(`CDN upload failed with status ${response.status}`);
  }

  return parseUploadUrl(response);
};

const reportUploadFailure = (error: unknown) => {
  getAppStore().showToast(ErrorMessages.uploadToCDN, ToastType.Error);
  sendUploadFailureAnalytics(
    error instanceof Error ? error.message : ErrorMessages.uploadToCDN
  );
};

// Retry the upload up to `MAX_UPLOAD_ATTEMPTS` times for transient failures only
// (network blips, 5xx). Auth is already refreshed-and-retried once inside
// `requestUpload`, and a surviving 401 won't recover — so those fail immediately.
// Resolves the CDN URL, or `null` once retries are exhausted.
const attemptUpload = async (
  file: File,
  attempt = 1
): Promise<string | null> => {
  try {
    return await requestUpload(file);
  } catch (error) {
    if (
      error instanceof UnauthorizedUploadError ||
      attempt >= MAX_UPLOAD_ATTEMPTS
    ) {
      reportUploadFailure(error);
      return null;
    }

    return attemptUpload(file, attempt + 1);
  }
};

const uploadToCDN = (file: File): Promise<string | null> => attemptUpload(file);

export default uploadToCDN;
